import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import { Icons } from "../components/Icons";

const STATUS_MAP = {
  open: { label: "Відкритий", color: "#3b82f6", bg: "rgba(59,130,246,0.1)", icon: "\u25CF" },
  in_progress: { label: "В роботі", color: "#f59e0b", bg: "rgba(245,158,11,0.1)", icon: "\u25CF" },
  resolved: { label: "Вирішено", color: "#10b981", bg: "rgba(16,185,129,0.1)", icon: "\u25CF" },
  closed: { label: "Закритий", color: "#6b7280", bg: "rgba(107,114,128,0.1)", icon: "\u25CF" },
};
const PRIORITY_MAP = {
  low: { label: "Low", color: "#6b7280" }, medium: { label: "Medium", color: "#f59e0b" },
  high: { label: "High", color: "#ef4444" }, urgent: { label: "Urgent", color: "#dc2626" },
};

export default function TicketsView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, projects, currentUser, activeId, proj } = useApp();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const commentsEndRef = useRef(null);

  // Form
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", priority: "medium" });
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplSearch, setTplSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const [tR, uR] = await Promise.all([
        apiFetch(`/api/tickets?project=${activeId}`).then(r => r.json()),
        apiFetch("/api/kanban/users").then(r => r.json()),
      ]);
      if (Array.isArray(tR)) setTickets(tR);
      if (Array.isArray(uR)) setUsers(uR);
    } catch {}
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const createTicket = async () => {
    if (!form.title.trim()) { flash("Назва обов'язкова"); return; }
    try {
      const r = await apiFetch("/api/tickets", { method: "POST", body: JSON.stringify({ ...form, project_id: activeId }) });
      const d = await r.json();
      if (d.id) { setTickets(prev => [d, ...prev]); setShowCreate(false); setForm({ title: "", description: "", assignee_id: "", priority: "medium" }); flash("Тікет створено"); }
      if (d.error) flash(d.error);
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const updateTicket = async (id, updates) => {
    try {
      const r = await apiFetch(`/api/tickets/${id}`, { method: "PUT", body: JSON.stringify(updates) });
      const d = await r.json();
      if (d.id) { setTickets(prev => prev.map(tk => tk.id === d.id ? d : tk)); if (openTicket?.id === d.id) setOpenTicket(d); }
    } catch {}
  };

  const deleteTicket = async (id) => {
    setTickets(prev => prev.filter(tk => tk.id !== id));
    if (openTicket?.id === id) setOpenTicket(null);
    try { await apiFetch(`/api/tickets/${id}`, { method: "DELETE" }); } catch {}
  };

  const loadComments = async (id) => {
    try { const r = await apiFetch(`/api/tickets/${id}/comments`); const d = await r.json(); if (Array.isArray(d)) setComments(d); } catch {}
  };

  const addComment = async () => {
    if (!commentText.trim() || !openTicket) return;
    try {
      const r = await apiFetch(`/api/tickets/${openTicket.id}/comments`, { method: "POST", body: JSON.stringify({ text: commentText.trim() }) });
      const d = await r.json();
      if (d.id) { setComments(prev => [...prev, d]); setCommentText(""); }
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  };

  const openModal = (ticket) => { setOpenTicket(ticket); setComments([]); setCommentText(""); loadComments(ticket.id); };

  const getUserName = (t, field) => t[field + "_full_name"] || t[field + "_name"]?.split("@")[0] || "";
  const ago = (d) => { if (!d) return ""; const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (m < 60) return `${m}хв`; const h = Math.floor(m / 60); if (h < 24) return `${h}год`; return `${Math.floor(h / 24)}д`; };

  // Find current user id from users list
  const myUser = users.find(u => u.username === currentUser);
  const myUserId = myUser?.id;

  const filtered = tickets.filter(tk => {
    if (filterStatus && tk.status !== filterStatus) return false;
    if (filterUser && tk.assignee_id !== parseInt(filterUser)) return false;
    if (filterClient === "client" && !tk.is_client) return false;
    if (filterClient === "internal" && tk.is_client) return false;
    if (filterClient === "my_assigned" && tk.assignee_id !== myUserId) return false;
    if (filterClient === "my_created" && tk.author_id !== myUserId) return false;
    return true;
  });

  const counts = { open: 0, in_progress: 0, resolved: 0, closed: 0 };
  tickets.forEach(tk => { if (counts[tk.status] !== undefined) counts[tk.status]++; });

  if (!proj) return null;
  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{Icons.ticket(16)} Тікети — {proj.name}</h2>
        <div style={{ display: "flex", gap: 6 }}>
          {Object.entries(counts).map(([k, v]) => {
            const s = STATUS_MAP[k];
            return <span key={k} style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: s.bg, color: s.color }}>{s.icon} {v}</span>;
          })}
        </div>
        <button onClick={async () => {
          setShowTemplates(true);
          if (!templates.length) {
            try { const r = await apiFetch("/api/task-templates/full"); const d = await r.json(); if (Array.isArray(d)) setTemplates(d); } catch {}
          }
        }} style={{ ...btnG, marginLeft: "auto", fontSize: 12 }}>{Icons.clipboard(12)} З шаблону</button>
        <button onClick={() => setShowCreate(true)} style={{ ...btnP, fontSize: 12 }}>＋ Новий тікет</button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, width: 130, fontSize: 11, padding: "4px 8px" }}>
          <option value="">Всі статуси</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inp, width: 150, fontSize: 11, padding: "4px 8px" }}>
          <option value="">Всі виконавці</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}</option>)}
        </select>
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ ...inp, width: 140, fontSize: 11, padding: "4px 8px" }}>
          <option value="">Всі тікети</option>
          <option value="my_assigned">Мені призначені</option>
          <option value="my_created">Мої створені</option>
          <option value="client">Від клієнтів</option>
          <option value="internal">Внутрішні</option>
        </select>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={{ ...card, padding: 20, marginBottom: 14, border: "1px solid #6366f133" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Новий тікет</div>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Назва" autoFocus style={{ ...inp, fontSize: 13, padding: "8px 12px", marginBottom: 8 }} />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Опис" rows={3} style={{ ...inp, fontSize: 12, padding: "8px 12px", marginBottom: 8, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <select value={form.assignee_id} onChange={e => setForm(f => ({ ...f, assignee_id: e.target.value }))} style={{ ...inp, flex: 1, fontSize: 11, padding: "4px 8px" }}>
              <option value="">Виконавець</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}{u.position ? ` (${u.position})` : ""}</option>)}
            </select>
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={{ ...inp, width: 100, fontSize: 11, padding: "4px 8px" }}>
              {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={createTicket} style={{ ...btnP, fontSize: 12 }}>Створити</button>
            <button onClick={async () => {
              setShowTemplates(true);
              if (!templates.length) {
                try {
                  const r = await apiFetch("/api/task-templates/full");
                  const d = await r.json();
                  if (Array.isArray(d)) setTemplates(d);
                } catch {}
              }
            }} style={{ ...btnG, fontSize: 12 }}>{Icons.clipboard(12)} З шаблону</button>
            <button onClick={() => setShowCreate(false)} style={{ ...btnG, fontSize: 12 }}>Скасувати</button>
          </div>
        </div>
      )}

      {/* Template picker modal */}
      {showTemplates && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowTemplates(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#1a1a2e" : "#fff", borderRadius: 16, padding: 0, maxWidth: 600, width: "95%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Шаблони тікетів</div>
              <input value={tplSearch} onChange={e => setTplSearch(e.target.value)} placeholder="Пошук..." autoFocus
                style={{ ...inp, fontSize: 12, padding: "6px 10px" }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px" }}>
              {(() => {
                const filtered = templates.filter(tpl => !tplSearch || tpl.text.toLowerCase().includes(tplSearch.toLowerCase()) || (tpl.category || "").toLowerCase().includes(tplSearch.toLowerCase()));
                const byCategory = {};
                filtered.forEach(tpl => {
                  const cat = tpl.category || "Інше";
                  if (!byCategory[cat]) byCategory[cat] = [];
                  byCategory[cat].push(tpl);
                });
                return Object.entries(byCategory).map(([cat, items]) => (
                  <div key={cat} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: t.textMuted, marginBottom: 4 }}>{cat}</div>
                    {items.map(tpl => (
                      <div key={tpl.id} onClick={() => {
                        setForm(f => ({ ...f, title: tpl.text, description: tpl.description || "" }));
                        setShowTemplates(false);
                        if (!showCreate) setShowCreate(true);
                      }} style={{
                        padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 4,
                        border: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                        transition: "all .15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "#6366f1"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = t.border}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{tpl.text}</div>
                        {tpl.description && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 3, whiteSpace: "pre-wrap", maxHeight: 60, overflow: "hidden" }}>{tpl.description.slice(0, 150)}{tpl.description.length > 150 ? "..." : ""}</div>}
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${t.border}` }}>
              <button onClick={() => setShowTemplates(false)} style={{ ...btnG, fontSize: 12, width: "100%" }}>Закрити</button>
            </div>
          </div>
        </div>
      )}

      {/* Ticket list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.map(tk => {
          const st = STATUS_MAP[tk.status] || STATUS_MAP.open;
          const pr = PRIORITY_MAP[tk.priority] || PRIORITY_MAP.medium;
          return (
            <div key={tk.id} onClick={() => openModal(tk)} style={{
              ...card, padding: "12px 16px", margin: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
              borderLeft: `4px solid ${st.color}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{tk.title}</span>
                  {tk.is_client && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(168,85,247,0.1)", color: "#a855f7" }}>КЛІЄНТ</span>}
                  {tk.is_auto && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(99,102,241,0.12)", color: "#6366f1" }} title="Автоматична задача">🤖 АВТО</span>}
                  {tk.client_approval === "approved" && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(16,185,129,0.15)", color: "#10b981", display: "inline-flex", alignItems: "center", gap: 3 }}>{Icons.checkCircle(12, "#10b981")} УЗГОДЖЕНО</span>}
                  {tk.client_approval === "rejected" && <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(239,68,68,0.15)", color: "#ef4444", display: "inline-flex", alignItems: "center", gap: 3 }}>{Icons.xCircle(12, "#ef4444")} ВІДХИЛЕНО</span>}
                  {tk.spec_url && <span style={{ fontSize: 9, color: "#6366f1", display: "inline-flex", alignItems: "center", gap: 3 }}>{Icons.link(10, "#6366f1")} ТЗ</span>}
                </div>
                <div style={{ display: "flex", gap: 8, fontSize: 11, color: t.textMuted }}>
                  {tk.project_name && <span>{tk.project_name}</span>}
                  <span>→ {getUserName(tk, "assignee") || "не призначено"}</span>
                  <span>{ago(tk.created_at)}</span>
                  {tk.comment_count > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>{Icons.messageSquare(10)} {tk.comment_count}</span>}
                </div>
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: pr.color + "15", color: pr.color }}>{pr.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 8, background: st.bg, color: st.color }}>{st.label}</span>
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ textAlign: "center", padding: 30, color: t.textMuted }}>Немає тікетів</div>}
      </div>

      {/* Modal */}
      {openTicket && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setOpenTicket(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: 640, maxHeight: "85vh", display: "flex", flexDirection: "column",
            background: dark ? "#1a1a24" : "#fff", borderRadius: 16, border: `1px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,.4)", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{ padding: "18px 24px 12px", borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, flex: 1 }}>{openTicket.title}</h3>
                {openTicket.is_client && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(168,85,247,0.1)", color: "#a855f7" }}>КЛІЄНТ: {openTicket.client_name}</span>}
                {openTicket.is_auto && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(99,102,241,0.12)", color: "#6366f1" }}>🤖 Автоматична задача</span>}
                <button onClick={() => setOpenTicket(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: t.textMuted }}>✕</button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <select value={openTicket.status} onChange={e => updateTicket(openTicket.id, { status: e.target.value })} style={{ ...inp, width: 120, fontSize: 11, padding: "3px 6px" }}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
                <select value={openTicket.assignee_id || ""} onChange={e => updateTicket(openTicket.id, { assignee_id: e.target.value ? parseInt(e.target.value) : null })} style={{ ...inp, width: 150, fontSize: 11, padding: "3px 6px" }}>
                  <option value="">Виконавець</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username.split("@")[0]}</option>)}
                </select>
                <select value={openTicket.priority} onChange={e => updateTicket(openTicket.id, { priority: e.target.value })} style={{ ...inp, width: 90, fontSize: 11, padding: "3px 6px" }}>
                  {Object.entries(PRIORITY_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <div style={{ flex: 1 }} />
                <button onClick={() => deleteTicket(openTicket.id)} style={{ ...btnSm, color: "#ef4444", fontSize: 11 }}>Видалити</button>
              </div>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
              {/* Client approval status */}
              {openTicket.client_approval && openTicket.client_approval !== "pending" && (
                <div style={{
                  padding: "12px 16px", borderRadius: 10, marginBottom: 14, display: "flex", alignItems: "center", gap: 10,
                  background: openTicket.client_approval === "approved" ? (dark ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.05)") : (dark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)"),
                  border: `1px solid ${openTicket.client_approval === "approved" ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
                }}>
                  <span style={{ display: "inline-flex" }}>{openTicket.client_approval === "approved" ? Icons.checkCircle(24, "#10b981") : Icons.xCircle(24, "#ef4444")}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: openTicket.client_approval === "approved" ? "#10b981" : "#ef4444" }}>
                      {openTicket.client_approval === "approved" ? "Клієнт узгодив" : "Клієнт відхилив"}
                    </div>
                    {openTicket.approval_at && <div style={{ fontSize: 11, color: t.textMuted }}>{new Date(openTicket.approval_at).toLocaleString("uk-UA")}</div>}
                  </div>
                </div>
              )}
              {openTicket.client_approval === "pending" && openTicket.spec_url && (
                <div style={{
                  padding: "10px 14px", borderRadius: 10, marginBottom: 14,
                  background: dark ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.04)",
                  border: "1px solid rgba(245,158,11,0.15)",
                  fontSize: 12, color: "#f59e0b", fontWeight: 600,
                }}>
                  {Icons.clock(14, "#f59e0b")} Очікує узгодження від клієнта
                </div>
              )}

              {/* Spec URL (TZ link) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{Icons.link(12)} Посилання на ТЗ</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    value={openTicket.spec_url || ""}
                    onChange={e => { const v = e.target.value; setOpenTicket(prev => ({ ...prev, spec_url: v })); }}
                    onBlur={e => updateTicket(openTicket.id, { spec_url: e.target.value })}
                    placeholder="https://docs.google.com/..."
                    style={{ ...inp, flex: 1, fontSize: 12, padding: "6px 10px" }}
                  />
                  {openTicket.spec_url && (
                    <a href={openTicket.spec_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#6366f1", textDecoration: "none", flexShrink: 0 }}>Відкрити ↗</a>
                  )}
                </div>
              </div>

              {openTicket.description && (
                <div style={{ padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 16 }}>
                  {openTicket.description}
                </div>
              )}

              <div style={{ display: "flex", gap: 16, fontSize: 11, color: t.textMuted, marginBottom: 16, padding: "8px 12px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)" }}>
                <span>Від: <strong style={{ color: t.text }}>{openTicket.is_client ? openTicket.client_name : getUserName(openTicket, "author")}</strong></span>
                <span>Проєкт: <strong style={{ color: t.text }}>{openTicket.project_name || "—"}</strong></span>
                <span>Створено: <strong style={{ color: t.text }}>{new Date(openTicket.created_at).toLocaleString("uk-UA")}</strong></span>
              </div>

              {/* Comments */}
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>{Icons.messageSquare(12)} Коментарі ({comments.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                {comments.map(c => (
                  <div key={c.id} style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: c.client_name ? "#a855f7" : "#6366f1" }}>
                        {c.client_name || c.author_full_name || c.author_name?.split("@")[0] || "—"}
                      </span>
                      {c.client_name && <span style={{ fontSize: 9, color: "#a855f7" }}>клієнт</span>}
                      <span style={{ fontSize: 10, color: t.textMuted }}>{new Date(c.created_at).toLocaleString("uk-UA")}</span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{c.text}</div>
                  </div>
                ))}
                <div ref={commentsEndRef} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Написати коментар..."
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                  rows={2} style={{ ...inp, flex: 1, fontSize: 12, padding: "8px 10px", resize: "none" }} />
                <button onClick={addComment} disabled={!commentText.trim()} style={{ ...btnP, fontSize: 11, padding: "8px 14px", alignSelf: "flex-end", opacity: commentText.trim() ? 1 : 0.5 }}>Надіслати</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
